// Client-side image compression for field photo capture.
//
// Evolves the CRMTIX Canvas compressor (SMRTCheques.tsx handleUploadFile)
// for SMRT COP's 50-70-photos-per-site scale: downscale to a max edge,
// re-encode as JPEG, and hand back a Blob/File ready to upload. Hardened
// over the CRMTIX version with three fixes that matter at this scale:
//
//   • EXIF-orientation correctness — the bitmap is decoded already-upright
//     (createImageBitmap { imageOrientation: "from-image" }) so re-encoding
//     can't leave a phone photo sideways (re-encode drops the EXIF flag).
//   • Explicit HEIC reject — the Canvas pipeline can't decode HEIC; rather
//     than store a giant unviewable original we throw so the caller can
//     prompt a retake.
//   • Output validation — iOS Safari can silently return a null/blank blob
//     when canvas limits are exceeded; we treat a missing/tiny blob as a
//     hard failure instead of saving a black image.
//
// IMPORTANT: compress photos ONE AT A TIME. Each decode briefly holds the
// full-resolution bitmap in memory; parallel decodes OOM-crash low-end
// devices. Callers should await one before starting the next.

export type ImageCompressionReason = "heic" | "not-image" | "decode" | "encode";

export class ImageCompressionError extends Error {
  reason: ImageCompressionReason;
  constructor(reason: ImageCompressionReason, message: string) {
    super(message);
    this.name = "ImageCompressionError";
    this.reason = reason;
  }
}

export interface CompressImageOptions {
  // Longest edge of the output, in pixels. 2000 keeps equipment labels /
  // serial numbers legible while cutting a multi-MB capture to a few hundred
  // KB. Default 2000.
  maxEdge?: number;
  // JPEG quality, 0-1. Default 0.8 — the legibility/size sweet spot; lower
  // the dimension before dropping below ~0.7.
  quality?: number;
  // Output file name. The extension is forced to .jpg regardless of input.
  fileName?: string;
}

const DEFAULT_MAX_EDGE = 2000;
const DEFAULT_QUALITY = 0.8;
// Below this the encode almost certainly failed (iOS silent blank canvas).
const MIN_VALID_BYTES = 1024;

function isHeic(file: File): boolean {
  const type = (file.type || "").toLowerCase();
  if (type === "image/heic" || type === "image/heif") return true;
  return /\.(heic|heif)$/i.test(file.name || "");
}

function withJpgExtension(name: string): string {
  return name.replace(/\.[^.]+$/, "") + ".jpg";
}

// Resize + re-encode `file` to a JPEG File. Resolves with the compressed
// File; rejects with an ImageCompressionError (check `.reason`) so callers
// can show the right message (e.g. "retake" for HEIC).
export async function compressImage(
  file: File,
  opts: CompressImageOptions = {},
): Promise<File> {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = opts.quality ?? DEFAULT_QUALITY;
  const outName = withJpgExtension(opts.fileName ?? file.name ?? "photo.jpg");

  if (file.type && !file.type.startsWith("image/") && !isHeic(file)) {
    throw new ImageCompressionError("not-image", "File is not an image.");
  }

  // Decode upright. `imageOrientation: "from-image"` bakes EXIF rotation
  // into the pixels. Cast: the option is valid at runtime on modern
  // browsers but missing from older TS DOM lib typings.
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(
      file,
      { imageOrientation: "from-image" } as unknown as ImageBitmapOptions,
    );
  } catch (err) {
    if (isHeic(file)) {
      throw new ImageCompressionError(
        "heic",
        "HEIC images can't be processed in the browser.",
      );
    }
    throw new ImageCompressionError(
      "decode",
      `Could not decode image: ${(err as Error)?.message || String(err)}`,
    );
  }

  try {
    const srcW = bitmap.width;
    const srcH = bitmap.height;
    const scale = Math.min(1, maxEdge / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new ImageCompressionError("encode", "Canvas 2D context unavailable.");
    }
    ctx.drawImage(bitmap, 0, 0, w, h);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality),
    );

    // iOS Safari can silently produce null / a tiny blank blob when canvas
    // limits are exceeded — treat that as a hard failure, not a saved image.
    if (!blob || blob.size < MIN_VALID_BYTES) {
      throw new ImageCompressionError(
        "encode",
        "Image encode produced no usable output.",
      );
    }

    return new File([blob], outName, { type: "image/jpeg" });
  } finally {
    bitmap.close();
  }
}
